import queryString from 'query-string';
import type { User } from '@atlassian/jira-common-directory-v2/src/model/index.tsx';
import type { Filter } from '@atlassian/jira-filters-common/src/model/index.tsx';
import type { SharePermission } from '@atlassian/jira-shared-types/src/share-permission.tsx';
import type { State, FiltersState } from '../../types.tsx';
import { getXsrfToken } from '../app/index.tsx';

export const isEmpty = (state: State): boolean => state.filters.order.length === 0;
export const getIds = (state: State): string[] => state.filters.order;
export const getTotal = (state: State): number => state.filters.total;
export const isLoading = (state: State): boolean => state.filters.isLoading;
export const isError = (state: State): boolean => state.filters.isError;
export const getName = (state: State, id: string): string | undefined =>
	state.filters.page[id]?.name;
export const getDescription = (state: State, id: string): string | undefined =>
	state.filters.page[id]?.description;
export const getOwner = (state: State, id: string): User | null | undefined =>
	state.filters.page[id]?.owner;
export const getViewUrl = (state: State, id: string): string | undefined =>
	state.filters.page[id]?.viewUrl;
export const getShares = (state: State, id: string): SharePermission[] | undefined =>
	state.filters.page[id]?.shares;
export const getEdits = (state: State, id: string): SharePermission[] | undefined =>
	state.filters.page[id]?.edits;
export const getFilter = (state: State, id: string): Partial<Filter> | undefined =>
	state.filters.page[id];
export const getIsWritable = (state: State, id: string): boolean =>
	state.filters.page[id]?.isWritable || false;
export const modalUrlFactory = (actionType: string) => (state: State, filterId: string) => {
	const xsrfToken = getXsrfToken(state);

	const parsed = queryString.stringify({
		atl_token: xsrfToken,
		returnUrl: 'ManageFilters.jspa',
		filterId,
	});

	return `/${actionType}!default.jspa?${parsed}`;
};

export const manageSubscriptionsUrl = modalUrlFactory('ViewSubscriptions');

const getFavoriteChangeContext = (state: State) => state.app.props.favoriteChangeContext;

export const getChangeFavorite = (state: State) => {
	// TODO EM-312: remove `| void` checks (e.g. `context &&`) from this and other directory pages when old provider is removed
	const context = getFavoriteChangeContext(state);
	return context && context.changeFavorite;
};

const getChangedFilter = (state: State, id: string) => {
	const context = getFavoriteChangeContext(state);
	// @ts-expect-error - TS2532 - Object is possibly 'undefined'.
	return context && context.items.filters[id];
};

const getIsFavoriteFromState = (state: FiltersState, id: string): boolean | undefined =>
	state.page[id]?.isFavourite;
const getPopularityFromState = (state: FiltersState, id: string): number | undefined =>
	state.page[id]?.popularity;

export const isFavorite = (state: State, id: string): boolean | undefined => {
	const itemChanged = getChangedFilter(state, id);
	return itemChanged ? itemChanged.value : getIsFavoriteFromState(state.filters, id);
};

export const isPending = (state: State, id: string): boolean => {
	const item = getChangedFilter(state, id);

	return !!(item && item.pending);
};

export const getPopularity = (state: State, id: string): number | undefined => {
	const popularityInState = getPopularityFromState(state.filters, id);
	const isFavoriteInState = getIsFavoriteFromState(state.filters, id);
	const item = getChangedFilter(state, id);

	if (!item) return popularityInState;

	if (item.value && !isFavoriteInState && popularityInState) {
		return popularityInState + 1;
	}
	if (!item.value && isFavoriteInState && popularityInState) {
		return popularityInState - 1;
	}
	return popularityInState;
};
